import { useRef, useEffect } from "react";
import { Upload, Trash2, X } from "lucide-react";

const ResponsiveProfileImage = ({ imageUrls, alt, className }) => {
  const getImageUrl = (size) => {
    if (!imageUrls) return null;
    if (typeof imageUrls === "string") return imageUrls;
    return imageUrls[size] || imageUrls.medium || imageUrls.large;
  };

  const mediumUrl = getImageUrl("medium");
  const largeUrl = getImageUrl("large");

  return (
    <img
      src={mediumUrl || largeUrl}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
    />
  );
};

export default function ProfileImageModal({
  previewImage,
  userData,
  onClose,
  onUploadClick,
  onRemoveClick,
  hasProfileImage,
  isUploading,
  isRemoving,
}) {
  const modalRef = useRef(null);

  // Close modal on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && e.target === modalRef.current) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onClose]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
    >
      {/* Modal Content */}
      <div
        className="
          w-full sm:w-96 bg-white rounded-t-3xl sm:rounded-2xl 
          shadow-2xl overflow-hidden 
          animate-in slide-in-from-bottom-5 duration-300 sm:scale-in
        "
      >
        {/* Close Button - Desktop Only */}
        <div className="hidden sm:flex justify-end pt-1 px-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Image Preview Section */}
        <div className="flex flex-col items-center p-6 sm:p-8">
          {/* Profile Image */}
          <div className="mb-6 flex justify-center">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
              {previewImage || userData.profileImage ? (
                typeof previewImage === "string" ? (
                  <img
                    src={previewImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ResponsiveProfileImage
                    imageUrls={previewImage || userData.profileImage}
                    alt={userData.name}
                    className="w-full h-full object-cover"
                  />
                )
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <span className="text-slate-600 font-semibold text-3xl sm:text-4xl">
                    {getInitials(userData.name)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Instruction Text */}
          <p className="text-center text-gray-600 text-sm">
            {hasProfileImage
              ? "Update or remove your profile picture"
              : "Add a profile picture"}
          </p>
        </div>

        {/* Action Buttons - Left Aligned */}
        <div className="flex flex-col">
          {/* Upload Button */}
          <button
            onClick={onUploadClick}
            disabled={isUploading || isRemoving}
            className="
              w-full flex items-center gap-3 px-4 py-3 text-black
              disabled:opacity-50 disabled:cursor-not-allowed
              font-medium text-sm
            "
          >
            {isUploading ? (
              <>
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload size={20} />
                <span>Upload Profile Picture</span>
              </>
            )}
          </button>

          {/* Remove Button - Only show if has profile image */}
          {hasProfileImage && (
            <button
              onClick={onRemoveClick}
              disabled={isUploading || isRemoving}
              className="
                w-full flex items-center gap-3 px-4 py-3 hover:bg-red-100 text-red-600 
                disabled:opacity-50 disabled:cursor-not-allowed
                font-medium text-sm
              "
            >
              {isRemoving ? (
                <>
                  <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                  <span>Removing...</span>
                </>
              ) : (
                <>
                  <Trash2 size={20} />
                  <span>Remove Picture</span>
                </>
              )}
            </button>
          )}

          {/* Close Button - Mobile Only */}
          <div className="sm:hidden px-5 py-3">
            <p className="text-sm text-gray-700 font-medium">
              Your profile picture will be visible to everyone on Wryven.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}